import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useSite } from "@/context/SiteContext";
import { mediaUrl } from "@/lib/usePage";
import { PlayCircle, LifeBuoy, X } from "lucide-react";

const SEEN_KEY = "trilogy_welcome_seen_v1";

// Convert a YouTube/Vimeo watch URL into an embeddable URL. Direct file URLs
// (.mp4/.webm) are returned as-is and rendered with a native <video> element.
const toEmbed = (raw) => {
  const u = String(raw || "").trim();
  if (!u) return { kind: "none", src: "" };
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(u)) return { kind: "file", src: mediaUrl(u) };
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?rel=0` };
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { kind: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  return { kind: "iframe", src: u };
};

const usePlayableVideos = () => {
  const { settings } = useSite();
  return useMemo(() => {
    if (!settings || settings.welcome_video_enabled === false) return [];
    const list = Array.isArray(settings.home_videos) ? settings.home_videos : [];
    return list
      .filter((v) => v && v.visible !== false && String(v.url || "").trim())
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [settings]);
};

// The pop-out modal itself (lazy: video only mounts while open).
const VideoModal = ({ open, onOpenChange, videos }) => {
  const featuredIdx = Math.max(0, videos.findIndex((v) => v.featured));
  const [active, setActive] = useState(featuredIdx);
  useEffect(() => { if (open) setActive(Math.max(0, videos.findIndex((v) => v.featured))); }, [open, videos]);

  const current = videos[active] || videos[0];
  const media = current ? toEmbed(current.url) : { kind: "none" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl overflow-hidden border-border bg-card p-0"
        data-testid="welcome-video-modal"
      >
        <DialogTitle className="sr-only">{current?.title || "Welcome video"}</DialogTitle>
        <DialogDescription className="sr-only">{current?.description || "Trilogy welcome video"}</DialogDescription>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Close welcome video"
          data-testid="welcome-video-close"
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="aspect-video w-full bg-black">
          {open && current && media.kind === "iframe" && (
            <iframe
              key={media.src}
              src={media.src}
              title={current.title || "Welcome video"}
              loading="lazy"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="h-full w-full border-0"
              data-testid="welcome-video-iframe"
            />
          )}
          {open && current && media.kind === "file" && (
            <video
              key={media.src}
              src={media.src}
              controls
              playsInline
              preload="metadata"
              poster={current.poster ? mediaUrl(current.poster) : undefined}
              className="h-full w-full bg-black"
              data-testid="welcome-video-player"
            />
          )}
        </div>

        <div className="p-5">
          <p className="font-condensed text-xs font-semibold uppercase tracking-[0.15em] text-brand">{current?.category || "Welcome"}</p>
          <h2 className="display-title mt-1 text-2xl">{current?.title || "Welcome to Trilogy"}</h2>
          {current?.description && <p className="mt-1 text-sm text-muted-foreground">{current.description}</p>}

          {videos.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto pb-1" data-testid="welcome-video-list">
              {videos.map((v, i) => (
                <button
                  key={v.id || i}
                  type="button"
                  onClick={() => setActive(i)}
                  aria-current={i === active}
                  data-testid={`welcome-video-thumb-${i}`}
                  className={`group relative w-40 shrink-0 overflow-hidden rounded-lg border text-left transition-colors ${i === active ? "border-[hsl(var(--brand-red))]" : "border-border hover:border-muted-foreground"}`}
                >
                  <div className="aspect-video w-full bg-muted">
                    {v.poster ? (
                      <img src={mediaUrl(v.poster)} alt="" loading="lazy" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center"><PlayCircle className="h-7 w-7 text-muted-foreground" /></div>
                    )}
                  </div>
                  <span className="block truncate px-2 py-1.5 text-xs font-medium">{v.title || `Video ${i + 1}`}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Home overlay: first-visit welcome pop-out + always-available "Watch Welcome
// Video" and "Support" action buttons. Self-contained; does not modify sections.
export const HomeWelcome = () => {
  const videos = usePlayableVideos();
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback((v) => {
    setOpen(v);
    if (!v) { try { localStorage.setItem(SEEN_KEY, "1"); } catch (e) { /* ignore */ } }
  }, []);

  // First-visit auto pop-out (only if there is a playable video and not seen).
  useEffect(() => {
    if (videos.length === 0) return;
    let seen = false;
    try { seen = localStorage.getItem(SEEN_KEY) === "1"; } catch (e) { seen = false; }
    if (seen) return;
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [videos.length]);

  return (
    <>
      <VideoModal open={open} onOpenChange={handleOpenChange} videos={videos} />

      {/* Sticky, unobtrusive action stack (bottom-left; chat widget uses bottom-right) */}
      <div className="fixed bottom-4 left-4 z-40 flex flex-col items-start gap-2 print:hidden">
        {videos.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Watch the Trilogy welcome video"
            data-testid="watch-welcome-video-btn"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card/90 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-foreground shadow-lg backdrop-blur transition-colors hover:border-[hsl(var(--brand-red))] hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98]"
          >
            <PlayCircle className="h-4 w-4 text-brand" /> Watch Welcome Video
          </button>
        )}
        <Link
          to="/support"
          aria-label="Need help? Contact Trilogy support"
          data-testid="home-support-btn"
          className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-white shadow-lg transition-colors hover:bg-[hsl(var(--brand-red-deep))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.98]"
        >
          <LifeBuoy className="h-4 w-4" /> Need Help? Support
        </Link>
      </div>
    </>
  );
};
